import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { scoreColor, isTopMaturity } from '../lib/score';
import { RowConfetti } from './RowConfetti';

export interface Top10Row {
  key: string;
  teamName: string;
  itemName: string;
  score: number;
  badge?: React.ReactNode;
  /** When present, the item name links here (e.g. the feature/repo detail page). */
  href?: string;
}

/** Client displays at most this many team names before collapsing the rest
 *  into a "+K more" suffix — a repo/feature owned by more teams than this
 *  still carries its full merged list, just not all of it inline.
 *  Chosen from the live distribution: of 111 multi-team repos, 86 span exactly
 *  2 teams (renders in full, no suffix) and only 25 span 3–7 (rare, capped). */
export const MAX_VISIBLE_TEAM_NAMES = 2;

/**
 * Cap a server-merged, comma-joined team-name list (`mergeTeamName` in
 * `rollup-sweep/src/units.ts`) to at most `max` names for display, appending
 * a `+K more` suffix for the rest — the full, untruncated list always comes
 * back in `title` so it stays reachable. Display-only: never mutates or
 * re-derives the merged value itself — the caller must keep rendering the
 * ORIGINAL `teamName` everywhere else (e.g. hrefs/ids). Shared by both the
 * Top-10 row and the (narrower) highlight cards so the cap can't drift
 * between the two surfaces.
 */
export function formatTeamLabel(
  mergedTeamName: string,
  max: number = MAX_VISIBLE_TEAM_NAMES,
): { text: string; title: string } {
  const names = mergedTeamName ? mergedTeamName.split(', ').filter(Boolean) : [];
  if (names.length <= max) {
    return { text: mergedTeamName, title: mergedTeamName };
  }
  const shown = names.slice(0, max).join(', ');
  const hidden = names.length - max;
  return { text: `${shown} +${hidden} more`, title: mergedTeamName };
}

/**
 * Ranked best-first "Top 10" list. Generic over the item — callers map repos or
 * features into Top10Row[]. No "see all"; the list is the full top slice.
 */
export function Top10List({
  title,
  itemHeader,
  rows,
  emptyText = 'No scored items yet.',
}: {
  title: string;
  itemHeader: string;
  rows: Top10Row[];
  emptyText?: string;
}): React.JSX.Element {
  const firstRowRef = useRef<HTMLTableRowElement>(null);
  const top = rows[0];
  return (
    <div className="card card-compact" data-testid="top10-list">
      <h3 className="text-lg font-semibold mb-md">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-secondary">{emptyText}</p>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th className="text-muted col-rank">#</th>
                <th className="text-muted">{itemHeader}</th>
                <th className="text-muted col-num">Score</th>
                <th className="text-muted col-num" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const team = row.teamName ? formatTeamLabel(row.teamName) : null;
                // The rank column below is rendered from `i` (score-sorted array
                // position), not from any field on `row` — so the key must be
                // equally robust: prefixing with `i` guarantees uniqueness even
                // if `row.key` (teamNumber:org/repo) ever collides (e.g. a
                // multi-team repo whose upstream identity wasn't fully deduped),
                // which otherwise lets React misassign DOM nodes across renders
                // and scramble which row shows which rank.
                return (
                  <tr key={`${i}-${row.key}`} data-testid="top10-row" ref={i === 0 ? firstRowRef : undefined}>
                    <td className="text-muted">{i + 1}</td>
                    <td>
                      <div className="stacked-cell">
                        {row.href ? (
                          <Link to={row.href} data-testid="top10-link" className="font-medium">
                            {row.itemName}
                          </Link>
                        ) : (
                          <span className="font-medium">{row.itemName}</span>
                        )}
                        {team && (
                          <span className="text-xs text-muted" title={team.title}>
                            {team.text}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="col-num col-score" style={{ color: scoreColor(row.score) }}>
                      {row.score}
                    </td>
                    <td className="col-num">{row.badge}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {top && isTopMaturity(top.score) && (
            <RowConfetti rowRef={firstRowRef} refreshKey={top.key} />
          )}
        </>
      )}
    </div>
  );
}
